import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { compact, difference, isEmpty } from "lodash-es";
import { Observable } from "rxjs";
import { distinctUntilChanged, filter, map, take } from "rxjs/operators";

import { CoreContentState } from "../state";

/**
 * @deprecated - [fabric-deprecated]
 */
@Injectable({
	providedIn: "root",
})
export class TranslationSelector {
	private logger: Log;

	constructor(
		private service: TranslateService,
		loggingFactory: LoggingFactory
	) {
		this.logger = loggingFactory.get("translations");
	}

	/**
	 * @deprecated - [fabric-deprecated]
	 */
	getAllTranslations() {
		return (state: CoreContentState): Record<string, string> | undefined => state.translation.translations;
	}

	/**
	 * @deprecated - [fabric-deprecated] - Use documentRef.obgEventBus.retrieve(ContentValue.isTranslationBusy) instead
	 */
	isBusy() {
		return (state: CoreContentState): boolean => state.translation.isBusy;
	}

	/**
	 * Gets translation by key.
	 * Will not resolve until translation is available.
	 * Will resolve once because it needs to wait until translations are loaded, they won't change later.
	 * @deprecated - [fabric-deprecated] - Use documentRef.obgEventBus.retrieve(ContentValue.getLoadedTranslationByKey) instead
	 */
	getByKey$(key: string, interpolateParams?: object) {
		return (state$: Observable<CoreContentState>): Observable<string> =>
			state$.pipe(
				distinctUntilChanged(undefined!, this.getAllTranslations()),
				map(() => this.service.instant(key, interpolateParams)),
				filter((result) => result !== key),
				take(1)
			);
	}

	/**
	 * @deprecated this method was deprecated due to some limitation, please use getByKeys_v2$ instead
	 * Returns array of translated strings in same sequence as keys.
	 * Will resolve once all requiredGroups available.
	 * Will resolve once because it needs to wait until translations are loaded, they won't change later
	 * @deprecated - [fabric-deprecated] - Use documentRef.obgEventBus.retrieve(ContentValue.getLoadedTranslationsByKeys) instead
	 */
	getByKeys$(keys: string[], requiredGroups: string[], interpolateParams?: object) {
		return (state$: Observable<CoreContentState>): Observable<string[]> =>
			state$.pipe(
				distinctUntilChanged(undefined!, (state) => state.translation.translations),
				map((state) => {
					if (difference(requiredGroups, state.translation.groupsAvailable).length !== 0) {
						return [];
					}

					const translations = this.service.instant(keys, interpolateParams);
					const missingKeys: string[] = [];
					const translationsList: string[] = [];

					// convert dictionary to ordered array for easier usage
					for (const key of keys) {
						const translatedString = translations[key];
						if (translatedString === key) {
							missingKeys.push(key);
						}
						translationsList.push(translatedString);
					}

					if (compact(missingKeys).length > 0) {
						this.logger.warn("getByKeys$", "translations not found", missingKeys);
					}

					return translationsList;
				}),
				filter((result) => !!result.length),
				take(1)
			);
	}

	/**
	 * Returns array of translated strings in same sequence as keys.
	 * Will resolve once all requiredGroups available.
	 * Will resolve once because it needs to wait until translations are loaded, they won't change later
	 * If `isContentRequired` is set to true it will return undefined if key translation not found
	 * @deprecated - [fabric-deprecated] - Use documentRef.obgEventBus.retrieve(ContentValue.getLoadedTranslationsByKeys) instead
	 */
	getByKeys_v2$(keys: string[], requiredGroups: string[], interpolateParams?: object, isContentRequired = false) {
		return (state$: Observable<CoreContentState>): Observable<(string | undefined)[]> =>
			state$.pipe(
				distinctUntilChanged(
					(prevState, currState) =>
						prevState.translation.translations === currState.translation.translations &&
						prevState.translation.groupsAvailable === currState.translation.groupsAvailable
				),
				map((state) => {
					if (difference(requiredGroups, state.translation.groupsAvailable).length !== 0) {
						return [];
					}

					const translations = this.service.instant(keys, interpolateParams);
					const missingKeys: string[] = [];
					const translationsList: (string | undefined)[] = [];

					// convert dictionary to ordered array for easier usage
					for (const key of keys) {
						const translatedString = translations[key];
						if (translatedString === key) {
							missingKeys.push(key);
						}

						translatedString === key && isContentRequired
							? translationsList.push(undefined)
							: translationsList.push(translatedString);
					}

					const compactedMissingKeys = compact(missingKeys);
					if (compactedMissingKeys.length > 0) {
						this.logger.warn(
							"getByKeys_v2$",
							`translations not found for ${compactedMissingKeys.join(", ")}`
						);
					}

					return translationsList;
				}),
				filter((result) => !!result.length),
				take(1)
			);
	}

	/**
	 * @deprecated - [fabric-deprecated]
	 */
	filteredGroups(groups: string[]) {
		return (state: CoreContentState): string[] =>
			isEmpty(groups) ? [] : difference(groups, state.translation.groupsAvailable);
	}
}
